import React, { useState, useEffect, useRef } from "react";
import { McModal } from "@maersk-global/mds-react-wrapper/components-core/mc-modal";
import { McButton } from "@maersk-global/mds-react-wrapper";
import axios from "axios";
import { toast } from "react-toastify";

export default function DocumentUploadModal({ isOpen, appName, recordStatus, userEmail, onClose, onUploadSuccess }) {
  const [selectedFile, setSelectedFile] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const fileInputRef = useRef(null);

  // Determine channel based on record status
  const channel = recordStatus === "Green" ? "green" : "red";

  // Reset file selection when modal opens
  useEffect(() => {
    if (isOpen) {
      setSelectedFile(null);
      setUploadProgress(0);
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    }
  }, [isOpen]);

  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (file) {
      setSelectedFile(file);
    }
  };

  const handleUpload = async () => {
    if (!selectedFile) {
      toast.error("Please select a file to upload");
      return;
    }

    setIsUploading(true);
    setUploadProgress(0);

    try {
      // Step 1: Request upload URL
      const uploadUrlResponse = await axios.post(
        `${process.env.REACT_APP_BACKEND_URI}/api/redChannelDocs/${encodeURIComponent(appName)}/upload-url?channel=${channel}`,
        {
          fileName: selectedFile.name,
          contentType: selectedFile.type || "application/octet-stream",
        },
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("jwt_token")}`,
          },
        }
      );

      const { documentId, uploadUrl } = uploadUrlResponse.data;

      // Step 2: Upload file to Azure Blob Storage using PUT
      await axios.put(uploadUrl, selectedFile, {
        headers: {
          "Content-Type": selectedFile.type || "application/octet-stream",
          "x-ms-blob-type": "BlockBlob",
        },
        onUploadProgress: (progressEvent) => {
          const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          setUploadProgress(percentCompleted);
        },
      });

      // Step 3: Confirm upload
      await axios.post(
        `${process.env.REACT_APP_BACKEND_URI}/api/redChannelDocs/${encodeURIComponent(appName)}/${documentId}/confirm?channel=${channel}`,
        {},
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("jwt_token")}`,
          },
        }
      );

      toast.success("Document uploaded successfully");
      setSelectedFile(null);
      setUploadProgress(0);
      onUploadSuccess();
      onClose();
    } catch (error) {
      console.error("Error uploading document:", error);
      toast.error(error.response?.data?.error || "Failed to upload document");
    } finally {
      setIsUploading(false);
    }
  };

  const handleClose = () => {
    if (!isUploading) {
      setSelectedFile(null);
      setUploadProgress(0);
      onClose();
    }
  };

  return (
    <McModal
      heading="Upload Document"
      dimension="small"
      height={"auto"}
      padding="default"
      open={isOpen}
      closed={handleClose}
      closing={handleClose}
    >
      <div style={{ display: "flex", flexDirection: "column", gap: "16px"}}>
        <div>
        
          <div style={{
            position: "relative",
            border: "2px dashed #ccc",
            borderRadius: "8px",
            padding: "20px",
            textAlign: "center",
            backgroundColor: isUploading ? "#f5f5f5" : "#fafafa",
            transition: "all 0.3s ease",
            cursor: isUploading ? "not-allowed" : "pointer",
          }}>
            <input
              ref={fileInputRef}
              id="file-upload"
              type="file"
              onChange={handleFileSelect}
              disabled={isUploading}
              style={{
                position: "absolute",
                top: 0,
                left: 0,
                width: "100%",
                height: "100%",
                opacity: 0,
                cursor: isUploading ? "not-allowed" : "pointer",
              }}
            />
            <div style={{ pointerEvents: "none", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center" }}>
              {!selectedFile && (
                <mc-icon icon="tray-arrow-up" style={{ fontSize: "3rem", color: "#0277bd", marginBottom: "12px", display: "block" }}></mc-icon>
              )}
              <div style={{ fontSize: "0.875rem", color: "#666", marginBottom: "4px" }}>
                {selectedFile ? (
                  <span style={{ color: "#0277bd", fontWeight: "600", fontSize: "1rem" }}>{selectedFile.name}</span>
                ) : (
                  "Click to browse or drag and drop"
                )}
              </div>
              {selectedFile && (
                <div style={{ fontSize: "0.75rem", color: "#999" }}>
                  {(selectedFile.size / 1024).toFixed(2)} KB
                </div>
              )}
            </div>
          </div>
        </div>

        {isUploading && (
          <div style={{ width: "100%" }}>
            <div style={{ fontSize: "0.875rem", color: "#666", marginBottom: "8px" }}>
              Uploading... {uploadProgress}%
            </div>
            <div style={{ width: "100%", height: "8px", backgroundColor: "#e0e0e0", borderRadius: "4px", overflow: "hidden" }}>
              <div
                style={{
                  width: `${uploadProgress}%`,
                  height: "100%",
                  backgroundColor: "#0277bd",
                  transition: "width 0.3s ease",
                }}
              />
            </div>
          </div>
        )}
      </div>

      <McButton
        slot="secondaryAction"
        appearance="neutral"
        fit="medium"
        click={handleClose}
        disabled={isUploading}
      >
        Cancel
      </McButton>
      <McButton
        slot="primaryAction"
        appearance="primary"
        fit="medium"
        click={handleUpload}
        disabled={!selectedFile || isUploading}
        loading={isUploading}
      >
        Upload
      </McButton>
    </McModal>
  );
}
